
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/* eslint-disable no-secrets/no-secrets */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLoaderData, useRevalidator } from 'react-router-dom';
import { Star as _Star, MapPin as _MapPin, Shield, Users as _Users, Award, Tent, Camera, Mountain, Star, MapPin, Users } from 'lucide-react';
import Header from '../../components/layout/Header';
import Breadcrumb from '../../components/layout/Breadcrumb';
import Footer from '../../components/layout/Footer';
import ImageGallery from '../../components/guide/ImageGallery';
import UrgencyIndicators from '../../components/shared/UrgencyIndicators';
import BookingCard from '../../components/guide/BookingCard';
import GuideCard from '../../components/guide/GuideCard';
import ReviewsSection from '../../components/shared/ReviewsSection';
import ServiceInfoRenderer from '../../components/serviceInfo/ServiceInfoRenderer';
import BottomSheet from '../../components/shared/BottomSheet';
import { useTheme } from '../../hooks/useTheme';
import { useBookingHold } from '../../hooks/useBookingHold';

/**
 * ExperienceDetailStatic Component
 * Individual experience detail page (static version, copy of GuideListingPage)
 * Contains test UUIDs (not real secrets)
 */
export default function ExperienceDetailStatic({ onAuthClick }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { experienceId } = useParams();
  const { experienceDetails, isOwner } = useLoaderData();
  const [isMobile, setIsMobile] = useState(false);
  const [isSticky, setIsSticky] = useState(true);
  const [showReserveInHeader, setShowReserveInHeader] = useState(false);
  const [showMobileBookingSheet, setShowMobileBookingSheet] = useState(false);

  // Lifted booking state - shared between BookingCard and sticky footer
  const [selectedDate, setSelectedDate] = useState(null);
  const [guests, setGuests] = useState(2);
  const [showCalendar, setShowCalendar] = useState(false);

  // Review stats state (populated by ReviewsSection callback)
  const [reviewStats, setReviewStats] = useState(null);

  // About section edit state
  const [isEditingAbout, setIsEditingAbout] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedDurationNumber, setEditedDurationNumber] = useState('');
  const [editedDurationInterval, setEditedDurationInterval] = useState('hours');
  const [editedMinParticipants, setEditedMinParticipants] = useState('');
  const [editedMaxParticipants, setEditedMaxParticipants] = useState('');
  const [isSavingAbout, setIsSavingAbout] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const reviewsRef = useRef(null);
  const bookingCardRef = useRef(null);
  const leftColumnRef = useRef(null);

  // Handle edit mode for About section
  const handleEditAbout = () => {
    setEditedDescription(listing.description || '');

    // Parse duration (e.g., "14 hours" -> number: 14, interval: "hours")
    const duration = experienceDetails?.duration || '';
    const durationMatch = duration.match(/^(\d+)\s+(\w+)$/);
    if (durationMatch) {
      setEditedDurationNumber(durationMatch[1]);
      setEditedDurationInterval(durationMatch[2]);
    } else {
      setEditedDurationNumber('');
      setEditedDurationInterval('hours');
    }

    // Parse group size (e.g., "1-6 people" -> min: 1, max: 6)
    const groupSize = experienceDetails?.groupSize || '';
    const rangeMatch = groupSize.match(/^(\d+)-(\d+)/);
    if (rangeMatch) {
      setEditedMinParticipants(rangeMatch[1]);
      setEditedMaxParticipants(rangeMatch[2]);
    } else {
      // Try "Up to X people" format
      const upToMatch = groupSize.match(/^Up to (\d+)/);
      if (upToMatch) {
        setEditedMinParticipants('1');
        setEditedMaxParticipants(upToMatch[1]);
      } else {
        setEditedMinParticipants(experienceDetails?.minParticipants?.toString() || '1');
        setEditedMaxParticipants(experienceDetails?.maxParticipants?.toString() || '');
      }
    }

    setIsEditingAbout(true);
  };

  const handleSaveAbout = async () => {
    setIsSavingAbout(true);
    setSaveError(null);

    try {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setSaveError('You must be logged in to edit this experience');
        setIsSavingAbout(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/experiences/${experienceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          description: editedDescription,
          durationNumber: editedDurationNumber,
          durationInterval: editedDurationInterval,
          minParticipants: parseInt(editedMinParticipants) || 1,
          maxParticipants: parseInt(editedMaxParticipants) || 1
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update experience');
      }

      // Refresh the page data
      revalidator.revalidate();

      // Exit edit mode
      setIsEditingAbout(false);
    } catch (error) {
      console.error('Error saving about section:', error);
      setSaveError(error.message);
    } finally {
      setIsSavingAbout(false);
    }
  };

  const handleCancelAbout = () => {
    setIsEditingAbout(false);
  };

  // Use real data from API or fallback to mock data
  const listing = experienceDetails ? {
    id: experienceDetails.id,
    guideId: experienceDetails.guideId,
    guideServiceId: experienceDetails.id,
    title: experienceDetails.title,
    rating: experienceDetails.rating,
    reviewCount: experienceDetails.reviewCount,
    location: experienceDetails.location,
    verified: experienceDetails.verified,
    images: experienceDetails.images && experienceDetails.images.length > 0
      ? experienceDetails.images
      : ['https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop'],
    description: experienceDetails.description,
    pricePerPerson: experienceDetails.pricePerPerson,
    serviceFee: experienceDetails.serviceFee,
    minParticipants: experienceDetails.minParticipants || 1,
    maxParticipants: experienceDetails.maxParticipants || null,
    guide: experienceDetails.guide
  } : {
    // Fallback mock data if API data not available
    id: experienceId,
    guideId: 'mock-trip-00000000-0000-0000-0000-000000000004', // Test/example value (not a real secret)
    guideServiceId: experienceId,
    title: 'Rocky Mountain Summit Trek - 3 Day Adventure',
    rating: 4.92,
    reviewCount: 248,
    location: 'Rocky Mountain National Park, Colorado',
    verified: true,
    images: [
      'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=800&h=600&fit=crop',
      'https://images.unsplash.com/photo-1454496522488-7a8e488e8606?w=800&h=600&fit=crop'
    ],
    description: `Embark on an unforgettable 3-day summit trek through the majestic Rocky Mountains. This intermediate-level adventure takes you through pristine alpine meadows, past crystal-clear mountain lakes, and up to breathtaking summit views at 12,500 feet.

Our experienced guides will lead you through some of Colorado&apos;s most stunning terrain while ensuring your safety and comfort. You&apos;ll camp under the stars, learn wilderness skills, and create memories that will last a lifetime.

This trek is designed for intermediate hikers with some high-altitude experience. We provide all necessary camping equipment, meals, and safety gear. All you need to bring is your sense of adventure!`,
    pricePerPerson: 899,
    serviceFee: 89,
    guide: {
      id: 'mock-trip-00000000-0000-0000-0000-000000000004', // Test/example value (not a real secret)
      name: 'Jake Morrison',
      bio: 'With over 15 years of mountain guiding experience and certifications from AMGA and Wilderness First Responder, Jake has led hundreds of successful summit expeditions throughout the Rockies. His approach combines safety, education, and unforgettable experiences.',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=120&h=120&fit=crop&crop=face',
      stats: {
        reviews: 248,
        rating: 4.92,
        trips: 523
      }
    }
  };

  // Use booking hold hook at page level - ONE instance shared by all components
  const {
    hold,
    loading: holdLoading,
    error: holdError,
    createHold,
    releaseHold,
  } = useBookingHold({
    guideId: listing.guideId,
    guideServiceId: listing.guideServiceId
  });

  // Sync selectedDate when hold is restored
  React.useEffect(() => {
    if (hold && hold.date && !selectedDate) {
      // Format date from ISO to Calendar format
      const dateStr = hold.date.split('T')[0]; // Get YYYY-MM-DD part
      const [year, month, day] = dateStr.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
      const formattedDate = `${monthName} ${year} ${day}`;
      setSelectedDate(formattedDate);
    }
  }, [hold, selectedDate]);

  const urgencyData = [
    { type: 'viewing', highlight: '3 people', text: 'are viewing this right now' },
    { type: 'bookings', highlight: '12 times', text: 'Booked in the last 30 days' },
    { type: 'recent', highlight: '2 hours ago', text: 'Last booked' }
  ];

  const _reviews = [
    { id: 1, name: 'Sarah Johnson', date: 'October 2024', rating: 5, text: 'Jake was knowledgeable, patient, and made sure everyone in our group felt comfortable and safe. The views from the summit were breathtaking, and the whole journey was perfectly organized.', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face' },
    { id: 2, name: 'Michael Chen', date: 'September 2024', rating: 5, text: 'An absolutely incredible experience! The guide was professional and the scenery was stunning. Would highly recommend to anyone looking for an adventure.', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face' },
    { id: 3, name: 'Emily Rodriguez', date: 'September 2024', rating: 4, text: 'Great trip overall. The hiking was challenging but rewarding. Only minor issue was some equipment could have been newer, but didn\'t detract from the experience.', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face' },
    { id: 4, name: 'David Thompson', date: 'August 2024', rating: 5, text: 'Best outdoor experience I\'ve ever had. Jake made everyone feel welcome and safe. The meals were surprisingly good for camping!', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face' },
    { id: 5, name: 'Lisa Anderson', date: 'August 2024', rating: 5, text: 'Phenomenal guide and breathtaking views. Worth every penny. Already planning our next trip!', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=face' },
    { id: 6, name: 'James Wilson', date: 'July 2024', rating: 4, text: 'Really enjoyed the trek. Weather was a bit unpredictable but that\'s nature. Guide handled everything professionally.', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=face' }
  ];

  // TODO: Service info cards will come from API based on trip type
  // The API will return an array of card configurations specific to the service
  const serviceInfoCards = [
    {
      type: 'trailMap',
      data: {
        mapImage: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800&h=400&fit=crop',
        trailPoints: [
          { name: 'Meeting Point', color: '#059669' },
          { name: 'Base Camp', color: '#2563eb' },
          { name: 'Summit', color: '#9333ea' },
          { name: 'Photo Spots', color: '#d97706' }
        ]
      }
    },
    {
      type: 'elevationProfile',
      data: {
        elevationPoints: [
          { elevation: '8,500 ft', label: 'Trailhead', position: 'bottom-2 left-2' },
          { elevation: '12,500 ft', label: 'Base Camp (Day 1)', position: 'top-2 right-1/4' },
          { elevation: '8,500 ft', label: 'Summit (Day 2)', position: 'bottom-2 right-2' }
        ],
        keyLocations: [
          {
            type: 'meeting',
            name: 'Meeting Point',
            description: 'Rocky Mountain Trailhead, Estes Park, CO',
            details: 'Free parking available • Restrooms on-site'
          },
          {
            type: 'camp',
            name: 'Base Camp',
            description: '10,800 ft elevation • 4 miles from trailhead'
          },
          {
            type: 'summit',
            name: 'Summit',
            description: '12,500 ft elevation • 2 miles from base camp'
          }
        ],
        nearbyAmenities: [
          'Parking: Free at trailhead (50 spaces)',
          'Nearest town: Estes Park (12 miles)',
          'Hotels: 15-20 min drive from trailhead',
          'Gear shops: Multiple in Estes Park',
          'Cell service: Limited at trailhead, none at camp'
        ],
        meetingPointLink: 'https://maps.google.com'
      }
    }
  ];

  const breadcrumbItems = [
    { label: 'Home', to: '/' },
    { label: 'Mountain Guides', to: '/' }, // TODO: Update with actual category/search page
    { label: 'Colorado', to: '/' }, // TODO: Update with actual location filter page
    { label: 'Rocky Mountain Summit Trek' }
  ];

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (reviewsRef.current && bookingCardRef.current && !isMobile) {
        const reviewsTop = reviewsRef.current.getBoundingClientRect().top;
        const bookingCardHeight = bookingCardRef.current.offsetHeight;
        const stickyOffset = 96; // top-24 sticky position
        const buffer = 300; // Buffer to prevent card from going under header

        // The 300px buffer accounts for:
        // 1. Visual spacing between content sections
        // 2. Scroll momentum/smoothness
        // 3. Ensuring conversion happens before any visual overlap with header
        // This value works well across different content lengths and viewport sizes

        if (reviewsTop <= bookingCardHeight + stickyOffset + buffer) {
          setIsSticky(false);
          setShowReserveInHeader(true);
        } else {
          setIsSticky(true);
          setShowReserveInHeader(false);
        }
      }
    };

    // Initial check and on scroll
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isMobile]);

  // Calculate total price based on current guest count
  const totalPrice = listing.pricePerPerson * guests + listing.serviceFee;

  // Shared Reserve handler
  const handleReserve = async () => {
    if (!selectedDate || !hold) {
      // Validation failed - show calendar
      setShowCalendar(true);
      if (isMobile) {
        setShowMobileBookingSheet(true);
      }
      return;
    }

    // Navigate to booking flow with hold ID and booking details
    const params = new URLSearchParams({
      experienceId: listing.guideServiceId,
      guideId: listing.guideId,
      date: selectedDate,
      guests: guests.toString(),
      pricePerPerson: listing.pricePerPerson.toString(),
      serviceFee: listing.serviceFee.toString(),
      title: listing.title,
      minimumAge: (listing.minimumAge || 0).toString(),
    });

    navigate(`/booking/${hold.id}?${params.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAuthClick={onAuthClick}
        showSearch={true}
        showReserveButton={showReserveInHeader}
        reservePrice={totalPrice}
      />
      <Breadcrumb items={breadcrumbItems} />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Title and meta info */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{listing.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
              {!reviewStats ? (
                <span className="text-gray-400">Loading...</span>
              ) : reviewStats.totalReviews > 0 ? (
                <>
                  <span className="font-semibold">{Number(reviewStats.averageRating).toFixed(2)}</span>
                  <span className="text-gray-600">({reviewStats.totalReviews} {reviewStats.totalReviews === 1 ? 'review' : 'reviews'})</span>
                </>
              ) : (
                <span className="text-gray-600">No reviews yet</span>
              )}
            </div>
            <div className="flex items-center gap-1 text-gray-600">
              <MapPin className="w-4 h-4" />
              <span>{listing.location}</span>
            </div>
            {listing.verified && (
              <div className="flex items-center gap-1 text-rose-600">
                <Award className="w-4 h-4" />
                <span className="font-medium">Verified Guide</span>
              </div>
            )}
          </div>
        </div>

        {/* Image Gallery */}
        <ImageGallery images={listing.images} alt={listing.title} />

        {/* Urgency Indicators */}
        <UrgencyIndicators indicators={urgencyData} variant="compact" />

        {/* Main content grid */}
        <div className={isMobile ? '' : 'grid grid-cols-3 gap-8'}>
          <div ref={leftColumnRef} className={isMobile ? '' : 'col-span-2'}>
            {/* About section */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">About this experience</h2>
                {isOwner && !isEditingAbout && (
                  <button
                    onClick={handleEditAbout}
                    className="text-sm text-gray-700 hover:text-gray-900 transition-colors"
                  >
                    Edit
                  </button>
                )}
                {isOwner && isEditingAbout && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleCancelAbout}
                      disabled={isSavingAbout}
                      className="px-3 py-1 text-sm text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAbout}
                      disabled={isSavingAbout}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingAbout ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {saveError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800">{saveError}</p>
                </div>
              )}

              {/* View Mode */}
              {!isEditingAbout && (
                <>
                  {listing.description.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-gray-700 leading-relaxed mb-4">
                      {paragraph}
                    </p>
                  ))}

                  {/* Duration and Group Size */}
                  {(experienceDetails?.duration || experienceDetails?.groupSize) && (
                    <div className="flex gap-6 mt-4 pt-4 border-t border-gray-200">
                      {experienceDetails.duration && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Duration</div>
                          <div className="text-sm font-semibold text-gray-900">{experienceDetails.duration}</div>
                        </div>
                      )}
                      {experienceDetails.groupSize && (
                        <div>
                          <div className="text-xs text-gray-500 mb-1">Group Size</div>
                          <div className="text-sm font-semibold text-gray-900">{experienceDetails.groupSize}</div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Edit Mode */}
              {isEditingAbout && (
                <div className="space-y-4">
                  {/* Description textarea */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe your experience..."
                    />
                  </div>

                  {/* Duration fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Duration
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        max="24"
                        value={editedDurationNumber}
                        onChange={(e) => setEditedDurationNumber(e.target.value)}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="1-24"
                      />
                      <select
                        value={editedDurationInterval}
                        onChange={(e) => setEditedDurationInterval(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="hours">hours</option>
                        <option value="days">days</option>
                        <option value="weeks">weeks</option>
                        <option value="months">months</option>
                      </select>
                    </div>
                  </div>

                  {/* Group size fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Group Size
                    </label>
                    <div className="flex gap-2 items-center">
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Minimum</label>
                        <input
                          type="number"
                          min="1"
                          value={editedMinParticipants}
                          onChange={(e) => setEditedMinParticipants(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Min"
                        />
                      </div>
                      <span className="text-gray-500 mt-6">-</span>
                      <div className="flex-1">
                        <label className="block text-xs text-gray-500 mb-1">Maximum</label>
                        <input
                          type="number"
                          min="1"
                          value={editedMaxParticipants}
                          onChange={(e) => setEditedMaxParticipants(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Max"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* What&apos;s Included */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-xl font-bold mb-4">What&apos;s Included</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Shield className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">Professional Guide</div>
                    <div className="text-sm text-gray-600">Certified mountain guide with 15+ years experience</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Tent className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">Camping Equipment</div>
                    <div className="text-sm text-gray-600">Tents, sleeping bags, pads, and cooking gear</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">All Meals</div>
                    <div className="text-sm text-gray-600">Breakfast, lunch, dinner, and snacks for 3 days</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Camera className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">Professional Photos</div>
                    <div className="text-sm text-gray-600">High-quality images of your adventure</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Award className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">Park Permits</div>
                    <div className="text-sm text-gray-600">All necessary permits and fees included</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mountain className={`w-5 h-5 ${theme.iconColor} flex-shrink-0 mt-0.5`} />
                  <div>
                    <div className="font-medium">Safety Equipment</div>
                    <div className="text-sm text-gray-600">First aid, emergency communication, navigation</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Day-by-Day Itinerary */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-xl font-bold mb-4">Day-by-Day Itinerary</h2>
              <div className="space-y-6">
                <div className={`border-l-4 ${theme.borderPrimary} pl-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`font-bold ${theme.textPrimary}`}>Day 1</span>
                    <span className="text-gray-500">•</span>
                    <span className="font-semibold text-gray-900">Trailhead to Base Camp</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">8:00 AM - 5:00 PM</div>
                  <p className="text-gray-700 mb-3">
                    Meet at the Rocky Mountain Trailhead for gear check and safety briefing. Begin our 4-mile hike to base camp at 10,800 feet, gaining 2,300 feet of elevation. Set up camp, enjoy dinner under the stars, and prepare for summit day.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">4 miles</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">2,300 ft gain</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Alpine meadows</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Wildflowers</span>
                  </div>
                </div>

                <div className={`border-l-4 ${theme.borderPrimary} pl-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`font-bold ${theme.textPrimary}`}>Day 2</span>
                    <span className="text-gray-500">•</span>
                    <span className="font-semibold text-gray-900">Summit Day</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">5:00 AM - 7:00 PM</div>
                  <p className="text-gray-700 mb-3">
                    Early alpine start for summit push. 2-mile climb to 12,500 feet with 1,700 feet of elevation gain. Enjoy 360-degree views from the top, take photos, and carefully descend back to base camp. Celebrate with a special summit dinner.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">2 miles</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">1,700 ft gain</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Summit views</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Continental Divide</span>
                  </div>
                </div>

                <div className={`border-l-4 ${theme.borderPrimary} pl-4`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`font-bold ${theme.textPrimary}`}>Day 3</span>
                    <span className="text-gray-500">•</span>
                    <span className="font-semibold text-gray-900">Return to Trailhead</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">7:00 AM - 2:00 PM</div>
                  <p className="text-gray-700 mb-3">
                    Break camp after breakfast and begin descent to trailhead. 4-mile hike with plenty of photo opportunities. Celebrate your accomplishment at the parking area with group photos and certificates of completion.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">4 miles</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Descent</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Wildlife</span>
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">Celebration</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Card - Desktop only */}
          {!isMobile && (
            <div className="col-span-1">
              <BookingCard
                ref={bookingCardRef}
                guideId={listing.guideId}
                guideServiceId={listing.guideServiceId}
                pricePerPerson={listing.pricePerPerson}
                serviceFee={listing.serviceFee}
                minGuests={listing.minParticipants || 1}
                maxGuests={listing.maxParticipants || 99}
                unavailableDates={[5, 12, 19, 26]}
                limitedDates={[8, 15, 22]}
                isSticky={isSticky}
                selectedDate={selectedDate}
                onSelectedDateChange={setSelectedDate}
                guests={guests}
                onGuestsChange={setGuests}
                showCalendar={showCalendar}
                onShowCalendarChange={setShowCalendar}
                onReserve={handleReserve}
                hold={hold}
                holdLoading={holdLoading}
                holdError={holdError}
                onCreateHold={createHold}
                onReleaseHold={releaseHold}
              />
            </div>
          )}
        </div>

        {/* Guide Card - Full Width */}
        <div className="mt-8">
          <GuideCard guide={listing.guide} />
        </div>

        {/* Reviews Section - Full Width */}
        <div className="mt-8" ref={reviewsRef}>
          <ReviewsSection
            entityType="experience"
            entityId={experienceId}
            onWriteReview={() => console.log('Write review clicked')}
            onStatsLoaded={setReviewStats}
          />
        </div>

        {/* Service-Specific Info Cards - Full Width */}
        <ServiceInfoRenderer cards={serviceInfoCards} />
      </div>

      {/* Footer */}
      <Footer />

      {/* Mobile Sticky Footer */}
      {isMobile && (
        <>
          <div
            className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            role="region"
            aria-label="Booking summary and actions"
          >
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Left Section: Booking Summary (65% width) - Tappable */}
              <button
                onClick={() => setShowMobileBookingSheet(true)}
                className="flex-1 text-left active:bg-gray-50 rounded-lg transition-colors py-2 px-3 -mx-3"
                aria-expanded={showMobileBookingSheet}
                aria-controls="mobile-booking-sheet"
                aria-label={
                  selectedDate
                    ? `Edit booking details: ${selectedDate}, ${guests} ${guests === 1 ? 'guest' : 'guests'}`
                    : "Select dates to start booking"
                }
                role="button"
                style={{ cursor: 'pointer' }}
              >
                {/* Price */}
                <div className="flex items-baseline gap-2 mb-0.5">
                  <span className="text-lg font-bold text-gray-900">${totalPrice}</span>
                  <span className="text-sm text-gray-600">total</span>
                </div>

                {/* Details - Shows current booking state */}
                <div className="text-sm">
                  {!selectedDate ? (
                    // State A: No Selection
                    <span className="text-red-600 font-semibold">Select dates</span>
                  ) : (
                    // State B/C: Has Selection
                    <div className="text-gray-600 flex items-center gap-1.5">
                      <span>{selectedDate}</span>
                      <span>·</span>
                      <span>{guests} {guests === 1 ? 'guest' : 'guests'}</span>
                      {reviewStats && reviewStats.totalReviews > 0 && (
                        <>
                          <span>·</span>
                          <span className="flex items-center gap-0.5">
                            <span>⭐</span>
                            <span>{Number(reviewStats.averageRating).toFixed(2)}</span>
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </button>

              {/* Right Section: Reserve Button (35% width) - Uses shared handler */}
              <button
                onClick={handleReserve}
                disabled={!selectedDate || !hold || holdLoading}
                className={`${
                  !selectedDate || !hold || holdLoading
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : theme.buttonPrimary
                } font-semibold py-3 px-6 rounded-lg transition shadow-md whitespace-nowrap flex-shrink-0`}
                aria-label="Reserve this trip"
              >
                {holdLoading ? 'Processing...' : hold ? 'Reserve' : 'Reserve'}
              </button>
            </div>
          </div>

          {/* Mobile Booking Sheet */}
          <BottomSheet
            isOpen={showMobileBookingSheet}
            onClose={() => {
              setShowMobileBookingSheet(false);
              setShowCalendar(false); // Reset calendar state when booking sheet closes
            }}
            title="Book this experience"
            initialHeight={60}
            expandedHeight={92}
          >
            <BookingCard
              ref={bookingCardRef}
              guideId={listing.guideId}
              guideServiceId={listing.guideServiceId}
              pricePerPerson={listing.pricePerPerson}
              serviceFee={listing.serviceFee}
              minGuests={listing.minParticipants || 1}
              maxGuests={listing.maxParticipants || 99}
              unavailableDates={[5, 12, 19, 26]}
              limitedDates={[8, 15, 22]}
              isSticky={false}
              selectedDate={selectedDate}
              onSelectedDateChange={setSelectedDate}
              guests={guests}
              onGuestsChange={setGuests}
              showCalendar={showCalendar}
              onShowCalendarChange={setShowCalendar}
              onReserve={handleReserve}
              hold={hold}
              holdLoading={holdLoading}
              holdError={holdError}
              onCreateHold={createHold}
              onReleaseHold={releaseHold}
            />
          </BottomSheet>
        </>
      )}
    </div>
  );
}
